import { enqueueSnackbar } from 'notistack'
import { axiosInstance } from './axiosInstance'
import { AxiosResponse } from 'axios'
import {
    ApplicationApiResponse,
    IOrgformData,
    OrgApiResponse,
    ShareOrgListApiResponse,
    ShareOrgPayload,
} from '../components/Organizations/interfaces'
import i18n from '../i18n'
import { Organization } from '../components/Layout/Navbar/interfaces'

const handleResponse = <T>(
    response: AxiosResponse<T>,
    successMessage?: string,
): T => {
    if (response.status >= 200 && response.status < 300) {
        if (response && successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return response.data
    } else {
        throw new Error('Error')
    }
}

const handleError = (err: any, errorMessage?: string) => {
    const message =
        err?.response?.data?.message || errorMessage || 'An error occurred'
    enqueueSnackbar(message, { variant: 'error' })
    // throw new Error(message)
}

export const makeRequest = async <T>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: unknown,
    params?: unknown,
    successMessage?: string,
    errorMessage?: string,
): Promise<T> => {
    try {
        const response: AxiosResponse<T> = await axiosInstance({
            method,
            url,
            data,
            params,
        })
        return handleResponse(response, successMessage)
    } catch (err) {
        handleError(err, errorMessage)
        return undefined as unknown as T
    }
}

export const fetchOrgs = async (
    params: unknown,
    orgId: string,
): Promise<OrgApiResponse> => {
    return makeRequest('get', `/organization?orgId=${orgId}`, null, params)
}

export const addOrg = async (
    data: IOrgformData,
    orgId: string,
): Promise<unknown> => {
    return makeRequest(
        'post',
        `/organization`,
        data,
        {
            orgId,
        },
        i18n.t('orgCreateSuccess'),
        i18n.t('orgCreateError'),
    )
}

export const updateOrg = async (
    data: IOrgformData,
    orgId: string,
): Promise<unknown> => {
    return makeRequest(
        'put',
        `/organization`,
        data,
        {
            orgId,
        },
        i18n.t('orgUpdateSuccess'),
        i18n.t('orgUpdateError'),
    )
}

export const deleteOrg = async (
    organizations: string[],
    orgId: string,
): Promise<unknown> => {
    return makeRequest(
        'delete',
        `/organization`,
        { organizations },
        {
            orgId,
        },
        i18n.t('orgDeleteSuccess'),
        i18n.t('orgDeleteError'),
    )
}

export const fetchAllOrgsShareDatabyOrgId = async (
    params: unknown,
    orgId: string,
): Promise<ShareOrgListApiResponse> => {
    return makeRequest('get', `/entityOrgs?ownerOrgId=${orgId}&`, null, params)
}

export const shareOrg = async (
    data: ShareOrgPayload,
    orgId: string,
): Promise<unknown> => {
    return makeRequest(
        'post',
        `/entityOrgs`,
        data,
        {
            orgId,
        },
        i18n.t('orgUpdateSuccess'),
        i18n.t('orgUpdateError'),
    )
}

export const fetchAllApplicationsList =
    async (): Promise<ApplicationApiResponse> => {
        return makeRequest('get', `/organization/accessFields`, null, null)
    }

export const fetchOrgDataOrgId = async (
    orgId: string,
): Promise<Organization> => {
    return makeRequest(
        'get',
        `/organization/organizationId?orgId=${orgId}`,
        null,
        null,
    )
}

export const fetchResolvedOrgDataOrgId = async (
    orgId: string,
    secondParamValue: string, //'Shipper' | 'Buyer' | 'product,originLocation,destinationLocation'
    secondParamKey: 'orgType' | 'entityType' = 'orgType',
): Promise<Organization> => {
    return makeRequest(
        'get',
        `/organization/resolveOrganizationId?orgId=${orgId}&${secondParamKey}=${secondParamValue}`,
        null,
        null,
    )
}

export const updateOrgUnits = async (
    data: { units: Record<string, string> },
    orgId: string,
): Promise<unknown> => {
    return makeRequest(
        'put',
        `/organization/updateUnits`,
        data,
        {
            orgId,
        },
        i18n.t('orgUnitsUpdateSuccess'),
        i18n.t('orgUnitsUpdateError'),
    )
}
